import React, { useCallback, useEffect, useMemo, useState } from "react";
import { X, FileText, FileSignature, BarChart3, MessageSquareText } from "lucide-react";
import { PIPELINE_STAGES } from "../constants/pipelineStages";
import {
  formatEngagementPercent,
  formatFollowers,
  formatRateInr,
  getInitials,
} from "../utils/pipelineFormatters";
import { getInfluencerByUsername } from "../../../../marketplace/services/marketplaceApi";

const formatActivityDate = (iso) => {
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(d);
  } catch {
    return "";
  }
};

function toPositiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function MetricTile({ label, value }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white px-2 py-2 lg:px-4 lg:py-3 shadow-sm text-center">
      <div className="text-[10px] lg:text-xs font-semibold text-gray-400 uppercase tracking-wide">
        {label}
      </div>
      <div className="mt-1 text-[10px] md:text-xs font-bold text-gray-900 tabular-nums">
        {value}
      </div>
    </div>
  );
}

function QuickAction({ icon: Icon, title, subtitle, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-xl border border-gray-100 bg-white px-2 py-2 lg:px-4 lg:py-3 shadow-sm hover:shadow-md hover:border-gray-200 transition-all"
    >
      <div className="flex items-start gap-3">
        <div className="lg:w-8 lg:h-8 w-6 h-6 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
          <Icon className="lg:w-5 lg:h-5 w-4 h-4 text-gray-700" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] lg:text-sm font-semibold text-gray-900">{title}</div>
          <div className="text-[8px] lg:text-[10px] text-gray-500 mt-0.5">{subtitle}</div>
        </div>
      </div>
    </button>
  );
}

function StagePills({ activeStageId }) {
  const activeIndex = useMemo(() => {
    const i = PIPELINE_STAGES.findIndex((s) => s.id === activeStageId);
    return i < 0 ? null : i;
  }, [activeStageId]);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {PIPELINE_STAGES.map((s, idx) => {
        const isActive = s.id === activeStageId;
        const isCompleted = activeIndex !== null && idx < activeIndex;
        return (
          <span
            key={s.id}
            className={[
              "px-2 lg:px-3 py-1 lg:py-1.5 rounded-full border text-[10px] lg:text-xs font-semibold transition-colors",
              isActive
                ? "bg-orange-500 text-white border-orange-500 shadow-sm"
                : isCompleted
                  ? "bg-[#E6F4EA] text-[#1F7A63] shadow-sm hover:bg-green-200 hover:border-green-300"
                : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50",
            ].join(" ")}
          >
            {s.label}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Right side drawer for the pipeline card details.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {import('../hooks/usePipelineBoard').PipelineCardModel | null} props.card
 * @param {import('../hooks/usePipelineBoard').PipelineActivityEntry[]} props.activities
 * @param {() => void} props.onClose
 * @param {(toStageId: string) => void} props.onChangeStage
 * @param {(text: string) => void} props.onAddActivity
 * @param {() => void} [props.onNegotiate]
 */
export default function PipelineDetailsDrawer({
  open,
  card,
  activities,
  onClose,
  onAddActivity,
  onContact,
  onNegotiate,
  onOpenContract,
  onOpenPerformance,
  onRemoveFromCampaign,
}) {
  const [note, setNote] = useState("");
  const [marketplaceEngagementRate, setMarketplaceEngagementRate] = useState(
    /** @type {number | null} */ (null)
  );

  const safeActivities = useMemo(() => activities ?? [], [activities]);

  const engagementRateValue = useMemo(
    () => toPositiveNumber(card?.engagementRate),
    [card?.engagementRate]
  );

  const usernameForLookup = useMemo(() => {
    const raw = String(card?.handle || "").trim();
    if (!raw) return "";
    return raw.replace(/^@/, "").trim().toLowerCase();
  }, [card?.handle]);

  useEffect(() => {
    let cancelled = false;

    setMarketplaceEngagementRate(null);

    if (!open || !usernameForLookup) return () => {};

    // If pipeline already has a non-zero engagement rate, keep it (avoid extra network call).
    if (engagementRateValue != null) return () => {};

    (async () => {
      try {
        const influencer = await getInfluencerByUsername(usernameForLookup);
        if (cancelled) return;
        const ig = influencer?.analytics?.Instagram;
        const rate = toPositiveNumber(ig?.engagementRate ?? ig?.engagement);
        setMarketplaceEngagementRate(rate);
      } catch {
        // non-blocking: keep UI stable if marketplace fetch fails
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, usernameForLookup, engagementRateValue]);

  const displayedEngagementRateValue = engagementRateValue ?? marketplaceEngagementRate;

  const onSaveNote = useCallback(() => {
    const trimmed = note.trim();
    if (!trimmed || !card?.id) return;
    onAddActivity(trimmed);
    setNote("");
  }, [note, card?.id, onAddActivity]);

  if (!open || !card) return null;

  const dealAction =
    card.stageId === "contracted" || card.stageId === "confirmed"
      ? {
          title: "Contract",
          subtitle: "Draft & e-sign",
          onClick: () => onOpenContract?.(),
        }
      : {
          title: "Negotiate",
          subtitle: "Pricing & deliverables",
          onClick: () => onNegotiate?.(),
        };

  return (
    <div className="fixed left-0 right-0 top-[72px] bottom-[56px] lg:bottom-0 z-[110]">
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-[1px]"
        role="presentation"
        onClick={onClose}
      />

      <aside className="absolute right-0 top-2 bottom-0 w-full sm:w-[420px] bg-gray-50 border-l border-gray-200 shadow-xl flex flex-col lg:mb-0">
        <div className="lg:p-5 p-2 bg-white border-b border-gray-100">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className="lg:w-11 lg:h-11 w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-orange-700">
                  {getInitials(card.name)}
                </span>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] lg:text-sm font-bold text-gray-900 truncate">
                  {card.name}
                </div>
                <div className="text-[10px] lg:text-xs text-gray-500 truncate">
                  {card.handle} · {card.platforms?.[0] ?? "Social"}
                </div>
                <div className="mt-2 inline-flex items-center rounded-full px-2 py-1 border text-[11px] font-semibold bg-orange-50 text-orange-700 border-orange-100">
                  {card.niches?.[0] ?? "Category"}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="lg:w-9 lg:h-9 w-6 h-6 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 flex items-center justify-center"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="lg:p-5 p-2 space-y-2 lg:space-y-5">
            <div>
              <div className="text-[10px] lg:text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-2">
                Metrics
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-3">
                <MetricTile label="Followers" value={formatFollowers(card.followers)} />
                <MetricTile
                  label="Engagement"
                  value={
                    displayedEngagementRateValue == null
                      ? "—"
                      : formatEngagementPercent(displayedEngagementRateValue)
                  }
                />
                <MetricTile label="Rate" value={formatRateInr(card.rate)} />
              </div>
            </div>

            <div>
              <div className="text-[10px] lg:text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-2">
                Quick actions
              </div>
              <div className="grid grid-cols-2 gap-2 lg:gap-3">
                <QuickAction
                  icon={MessageSquareText}
                  title="Contact"
                  subtitle="DM/ Email/ WhatsApp"
                  onClick={() => onContact?.()}
                />
                <QuickAction
                  icon={FileSignature}
                  title={dealAction.title}
                  subtitle={dealAction.subtitle}
                  onClick={dealAction.onClick}
                />
                <QuickAction
                  icon={BarChart3}
                  title="Performance"
                  subtitle="Views, ROI, posts"
                  onClick={() => onOpenPerformance?.()}
                />
                <QuickAction
                  icon={FileText}
                  title="Brief"
                  subtitle="Send campaign brief"
                  onClick={() => onAddActivity("Brief: opened")}
                />
              </div>
            </div>

            <div>
              <div className="text-[10px] lg:text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-2">
                Pipeline stage
              </div>
              <StagePills activeStageId={card.stageId} />
            </div>

            <div>
              <div className="text-[10px] lg:text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-2">
                Activity log
              </div>

              <div className="space-y-3">
                {safeActivities.map((a) => (
                  <div key={a.id} className="flex gap-2 lg:gap-3">
                    <div className="w-2 h-2 rounded-full bg-orange-400 mt-2 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[10px] lg:text-sm text-gray-900">{a.text}</div>
                      <div className="text-[8px] lg:text-xs text-gray-500 mt-0.5">
                        {formatActivityDate(a.atISO)}
                      </div>
                    </div>
                  </div>
                ))}

                {safeActivities.length === 0 && (
                  <div className="text-[10px] lg:text-sm text-gray-500">
                    No activity yet. Use actions or add a note.
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="text-[10px] lg:text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-2">
                Add note
              </div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Awaiting content draft..."
                className="w-full min-h-[88px] resize-none rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-300"
              />
              <div className="mt-3 flex items-center justify-end">
                <button
                  type="button"
                  onClick={onSaveNote}
                  className="px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 transition-colors shadow-sm"
                >
                  Save note
                </button>
              </div>

              <button
                type="button"
                onClick={() => onRemoveFromCampaign?.()}
                className="mt-4 w-full py-2.5 rounded-lg border border-red-200 bg-red-50 text-red-700 text-xs md:text-sm font-semibold hover:bg-red-100 transition-colors"
              >
                Remove from campaign
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

